/**
 * Cuánto hay que encoger una foto antes de subirla.
 *
 * El teléfono de un contratista dispara a 4032 px y 3-4 MB. Esa foto acaba en
 * una banda de portada que se pinta, como mucho, a 1600 px de ancho — así que
 * el 90 % de lo que se sube, se sube para tirarlo al pintar.
 *
 * Lo caro NO es guardarla. Guardar es de céntimos (ver la nota de costes en el
 * historial de esta decisión). Lo caro es que el cliente abre la propuesta
 * desde el móvil, en la entrada de su casa, con cobertura de barrio, y espera
 * ocho segundos a una foto que se iba a ver igual pesando 200 KB. Eso no es una
 * factura de infraestructura: es la propuesta que no se leyó.
 *
 * Reglas, y por qué:
 *  - GIF nunca se toca: re-codificar mata la animación.
 *  - Ya pequeña Y ya estrecha → se deja en paz. Re-codificar SIEMPRE pierde
 *    calidad; hacerlo sin ganar tamaño es puro daño.
 *  - Se encoge por ancho, nunca se agranda: subir una foto de 800 px no la
 *    convierte en una de 1920 px, la convierte en una de 800 px borrosa.
 *
 * La parte de canvas vive en services/storage.ts (necesita DOM). Aquí solo está
 * la decisión, que es la que se puede probar.
 */

/** Ancho máximo que se guarda. La banda del hero se pinta a ~1600 px; 1920 deja
 *  margen para pantallas densas sin guardar una foto de 4000 px. */
export const ANCHO_MAXIMO = 1920;

/** Por debajo de esto no vale la pena re-codificar aunque sobre ancho. */
export const UMBRAL_BYTES = 500 * 1024;

/** Calidad JPEG de salida. 0.85 es el punto donde el ojo deja de notar la
 *  diferencia en una foto y el archivo ya bajó un orden de magnitud. */
export const CALIDAD_JPEG = 0.85;

/** Formatos que nunca se re-codifican. */
const INTOCABLES = new Set(['image/gif']);

/**
 * ¿Merece la pena encoger esta foto?
 *
 * `ancho` es el de la imagen ya decodificada. Si todavía no se conoce, pásalo
 * como 0 y la decisión se toma solo por el peso.
 */
export function valeLaPenaEncoger(tipo: string, bytes: number, ancho: number): boolean {
    if (INTOCABLES.has(tipo)) return false;
    if (ancho > ANCHO_MAXIMO) return true;
    return bytes > UMBRAL_BYTES;
}

/**
 * Tamaño de destino conservando la proporción. Nunca agranda.
 *
 * Se redondea al entero más cercano y se garantiza un mínimo de 1 px: un canvas
 * de altura 0 lanza en algunos navegadores, y una foto panorámica muy extrema
 * llega a redondear a cero por abajo.
 */
export function dimensionesDestino(
    ancho: number,
    alto: number,
    maximo: number = ANCHO_MAXIMO,
): { ancho: number; alto: number } {
    if (!(ancho > 0) || !(alto > 0)) return { ancho: 0, alto: 0 };
    if (ancho <= maximo) return { ancho, alto };
    const escala = maximo / ancho;
    return {
        ancho: maximo,
        alto: Math.max(1, Math.round(alto * escala)),
    };
}
